/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import type { PlaylistClip } from "types/Clip";
import type { Asset } from "types/Asset";
import { createLogger, EnhancedLogger } from "utilities/Logger";

export abstract class AudioEffect {
  static canBuildFor(clip: PlaylistClip): boolean {
    return false;
  }

  static providePermanentUrl?(
    clip: PlaylistClip,
    asset: Asset
  ): string | undefined;

  static canUseLocalAsset?(): boolean;

  protected constructor(
    protected audioContext: AudioContext,
    protected clip: PlaylistClip,
    protected playlistTime: number,
    protected readonly logger: EnhancedLogger = createLogger(
      this.constructor.name // Use the name of the implementing class
    ).createSubLogger(clip.id)
  ) {}

  abstract hotswap(newClip: PlaylistClip): void;

  abstract get inputNode(): AudioNode;

  abstract get outputNode(): AudioNode;

  abstract disconnect(): void;
}

export type AudioEffectClass = {
  new (
    audioContext: AudioContext,
    clip: PlaylistClip,
    playlistTime: number,
    logger?: EnhancedLogger
  ): AudioEffect;
} & typeof AudioEffect;

// Helper type to identify effects that provide custom URLs
export type URLProvidingEffectClass = AudioEffectClass & {
  providePermanentUrl(clip: PlaylistClip, asset: Asset): string | undefined;

  canUseLocalAsset(): boolean;
};
